// Krug 5 (K9b): Scrap is the currency, shown in the game's top bar next to the level and energy.
// A tap explains it; when Scrap arrives (a claimed run, salvage, a contract) the number counts up
// and glows for a moment, without a toast.

import { useEffect, useRef, useState } from 'react';
import { t } from '@/i18n';
import { cx, num } from '@/lib/format';
import { Icon } from './Icon';

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** The shown number: counts up to a higher value over `ms`; a lower value is shown at once. */
function useCountUp(value: number, ms = 700): { shown: number; rising: boolean } {
  const [shown, setShown] = useState(value);
  const [rising, setRising] = useState(false);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current;
    from.current = value;
    if (value <= start || reducedMotion()) {
      setShown(value);
      if (value > start) {
        setRising(true);
        const id = setTimeout(() => setRising(false), 900);
        return () => clearTimeout(id);
      }
      return;
    }
    setRising(true);
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      setShown(Math.round(start + (value - start) * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const done = setTimeout(() => setRising(false), ms + 400);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(done);
      setShown(value);
    };
  }, [value, ms]);
  return { shown, rising };
}

export function ScrapCounter({ value, className }: { value: number; className?: string }) {
  const { shown, rising } = useCountUp(value);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', close);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div ref={box} className={cx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={t('common.scrapAria', { n: num(value) })}
        data-testid="scrap-counter"
        data-rising={rising || undefined}
        className="flex min-h-11 shrink-0 flex-col items-end justify-center gap-0.5 rounded-[6px] px-1 hover:bg-raised lg:px-1.5"
      >
        <span className="flex items-center gap-1 text-text">
          <Icon id="ui_scrap" size={16} />
          <span className={cx('mono inline-block text-[15px] font-bold tabular-nums transition-colors lg:text-[16px]', rising ? 'animate-scrap text-text' : 'text-text')}>{num(shown)}</span>
        </span>
        <span className="mono text-[11px] text-muted lg:text-[12px]">{t('common.scrap')}</span>
      </button>
      {open && (
        <div role="dialog" aria-label={t('common.scrap')} className="absolute right-0 top-full z-50 mt-1.5 w-[260px] animate-fade rounded-[6px] border border-border bg-surface p-3 text-[13px] leading-[19px] shadow-pop">
          <span className="mb-1 flex items-center gap-1.5 font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-text">
            <Icon id="ui_scrap" size={16} />
            {t('common.scrap')} · {num(value)}
          </span>
          {t('common.scrapInfo')}
        </div>
      )}
    </div>
  );
}
